import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Chip
} from '@mui/material';
import {
  Restore as RestoreIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import useProcessFlowStore from '../../stores/processFlowStore';
import { useSnackbar } from 'notistack';

function VersionHistory({ flowId, open, onClose }) {
  const { getVersions, restoreVersion, currentFlow } = useProcessFlowStore();
  const { enqueueSnackbar } = useSnackbar();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && flowId) {
      loadVersions();
    }
  }, [open, flowId]);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const data = await getVersions(flowId);
      setVersions(data.versions || []);
    } catch (error) {
      enqueueSnackbar('Failed to load version history', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (versionNumber) => {
    try {
      await restoreVersion(flowId, versionNumber);
      enqueueSnackbar('Version restored successfully', { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar('Failed to restore version', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Version History
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : versions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center">
            No version history available
          </Typography>
        ) : (
          <List>
            {versions.map((version) => (
              <ListItem key={version.version} divider>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2">
                        Version {version.version}
                      </Typography>
                      {version.version === currentFlow?.currentVersion && (
                        <Chip label="Current" size="small" color="primary" />
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2">
                        {version.changeDescription || 'No description'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {version.createdBy?.firstName} {version.createdBy?.lastName} •{' '}
                        {new Date(version.createdAt).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        {version.nodes?.length || 0} nodes, {version.edges?.length || 0} edges
                      </Typography>
                    </>
                  }
                />
                {version.version !== currentFlow?.currentVersion && (
                  <ListItemSecondaryAction>
                    <Button
                      startIcon={<RestoreIcon />}
                      onClick={() => handleRestore(version.version)}
                      size="small"
                    >
                      Restore
                    </Button>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default VersionHistory;